/**
 * Tab vocabulary for the community detail screen, extracted from CommunityDetail so
 * the tab-resolution rule is a pure, independently testable unit (the screen itself
 * is a heavy component with many hooks).
 */

export enum CommunityTab {
  Home = 'home',
  Members = 'members',
  Events = 'events',
  About = 'about',
  // The Partners page (TECH-86). Named "Perks" or "Partners" by `partnersTabLabel`; shown to
  // admins always and to everyone else only while there is something for them
  // (`canViewPartnersTab`). Gated in resolveCommunityTab like Members.
  Partners = 'partners',
  // Admin-only tab. Its visible trigger is appended at the CommunityDetail render site
  // (via `visibleTabs`, gated by `showActivation`), not in COMMUNITY_TABS — that array
  // is the all-members base set and the input to resolveCommunityTab.
  Activation = 'activation',
}

/**
 * The all-members base tab set and the input to resolveCommunityTab. Activation is
 * excluded here because it is admin-only; its trigger is appended at the render site
 * (CommunityDetail `visibleTabs`, gated by `showActivation`).
 */
export const COMMUNITY_TABS = [
  CommunityTab.Home,
  CommunityTab.Members,
  CommunityTab.Events,
  CommunityTab.About,
  CommunityTab.Partners,
] as const;

export const TAB_LABELS: Record<CommunityTab, string> = {
  [CommunityTab.Home]: 'Home',
  [CommunityTab.Members]: 'Members',
  [CommunityTab.Events]: 'Calendar',
  [CommunityTab.About]: 'About',
  // The fallback only: the strip names this tab with `partnersTabLabel`.
  [CommunityTab.Partners]: 'Partners',
  [CommunityTab.Activation]: 'Space Activation',
};

// All tab values (incl. the admin-only Activation tab), hoisted so the per-render
// `isCommunityTab` checks don't re-allocate. `?tab=activation` validates here, while
// resolveCommunityTab still gates it to admins via `showActivation`.
const ALL_TAB_VALUES = Object.values(CommunityTab) as string[];

export const isCommunityTab = (value: string | null): value is CommunityTab =>
  value !== null && ALL_TAB_VALUES.includes(value);

/** The partner numbers the tab reads, from the Space DETAIL response (`SpaceDetailRead`). */
export interface PartnersTabCounts {
  partners_visible_count?: number;
  members_only_perk_count?: number;
  members_only_sponsor_count?: number;
  partners_has_value?: boolean;
}

/**
 * Whether the viewer sees the Partners tab (TECH-86): admins always, including the empty page;
 * everyone else only while something is there for them, visible cards or members-only ones (a
 * page whose every card is members-only still shows a visitor the tab and the join gate). Reads
 * the Space detail, never the listing, so the tab cannot flicker while the listing loads; every
 * route that returns the detail carries the counts, so a Space save keeps the tab.
 */
export const canViewPartnersTab = (
  counts: PartnersTabCounts | null | undefined,
  isPartnersAdmin: boolean,
): boolean => {
  if (isPartnersAdmin) return true;
  if (!counts) return false;
  return (
    (counts.partners_visible_count ?? 0) +
      (counts.members_only_perk_count ?? 0) +
      (counts.members_only_sponsor_count ?? 0) >
    0
  );
};

/**
 * The Partners tab's name, the same for every viewer: "Perks" once any partnership in the Space
 * has a value (Space-wide, members-only ones included), "Partners" otherwise, the empty page too.
 */
export const partnersTabLabel = (counts: PartnersTabCounts | null | undefined): string =>
  counts?.partners_has_value ? 'Perks' : 'Partners';

/**
 * Resolve the active community tab from URL state, gating the admin-only Activation
 * tab so `activeTab` itself stays admin-correct — no consumer can read a "phantom"
 * Activation tab for a non-admin. A deep-linked `?tab=activation` resolves to
 * Activation only when `showActivation` (admin); otherwise it falls back to Home.
 * Likewise `?tab=members` falls back to Home unless `canViewMembers`, and `?tab=partners`
 * unless `canViewPartners` (see `canViewPartnersTab`).
 * A deep-linked event (`initialEventId`, initial mount only) takes precedence.
 */
export function resolveCommunityTab({
  tabParam,
  showActivation,
  canViewMembers,
  canViewPartners,
  initialEventId = null,
}: {
  tabParam: string | null;
  showActivation: boolean;
  canViewMembers: boolean;
  canViewPartners: boolean;
  initialEventId?: string | null;
}): CommunityTab {
  if (initialEventId) return CommunityTab.Events;
  if (
    isCommunityTab(tabParam) &&
    (tabParam !== CommunityTab.Activation || showActivation) &&
    (tabParam !== CommunityTab.Members || canViewMembers) &&
    (tabParam !== CommunityTab.Partners || canViewPartners)
  ) {
    return tabParam;
  }
  return CommunityTab.Home;
}
